import { useDispatch, useSelector } from "react-redux"
import { areWorkersRunning, getAvailableDevicesWorker, getCheckSavedAlbumsChangesWorker, getDeactivateShuffleModeWorker, getTooManyRequestsCooldownWorker, getUpdatePlaybackWorker } from "../../reducers/workers"
import { updatePlaybackState } from "../../actions/playbackStateActions";
import { getSavedAlbumsLoadingProgress, getTooManyRequestsCooldown } from "../../reducers/disablingProcesses";
import { checkChangesOnSavedAlbums, putShuffleMode } from "../../controllers/SpotifyApiController";
import { setSavedAlbumWarnings } from "../../actions/albumListActions";
import { getPointedDeviceId } from "../../reducers/devices";
import { getShuffleState } from "../../reducers/playbackState";
import { getRandomModeActive } from "../../reducers/randomMode";
import { useEffect } from "react";
import { getSavedAlbums } from "../../reducers/albumListState";
import { setAvailableDevices } from "../../actions/devicesActions";
import { clearTooManyRequestsBlocking } from "../../utils/localStorageUtils";
import { setTooManyRequestsCooldown } from "../../actions/disablingProcessesActions";
import { manageTooManyRequestsResponse } from "../../utils/connectionUtils";

export const useManageWorkers = () => {
    const running = useSelector(areWorkersRunning);
    const dispatch = useDispatch();

    const useUpdatePlaybackState = () => {
        const worker = useSelector(getUpdatePlaybackWorker);

        useEffect(() => {
            if(!running || !worker)
            {
                return;
            }

            worker.onmessage = () => {
                dispatch(updatePlaybackState());
            }

            worker.postMessage('start');

        // eslint-disable-next-line
        }, [running, worker]);
    }

    const useCheckSavedAlbumsChanges = () => {
        const worker = useSelector(getCheckSavedAlbumsChangesWorker);
        const savedAlbums = useSelector(getSavedAlbums);
        const savedAlbumsLoadingProgress = useSelector(getSavedAlbumsLoadingProgress);

        useEffect(() => {
            if(!running || !worker)
            {
                return;
            }

            if(savedAlbumsLoadingProgress !== 100)
            {
                worker.postMessage('stop');
                return;
            }

            worker.onmessage = () => {
                checkChangesOnSavedAlbums(savedAlbums)
                    .then((result) => {
                        const { areAlbumsToAdd, areDeletedAlbums } = result;

                        dispatch(setSavedAlbumWarnings(areAlbumsToAdd, areDeletedAlbums));
                    })
                    .catch((error) => { 
                        manageTooManyRequestsResponse(error, dispatch);
                        console.error("Error while looking for new added saved albums: ", error);
                    });
            }

            worker.postMessage('start');

        // eslint-disable-next-line
        }, [running, worker, savedAlbumsLoadingProgress]);
    }

    const useDeactivateShuffleMode = () => {
        const worker = useSelector(getDeactivateShuffleModeWorker);
        const pointedDeviceId = useSelector(getPointedDeviceId);
        const shuffleState = useSelector(getShuffleState);
        const randomModeActive = useSelector(getRandomModeActive);

        useEffect(() => {
            if(!running || !worker)
            {
                return;
            }

            if(!randomModeActive || !pointedDeviceId)
            {
                worker.postMessage('stop');
                return;
            }

            worker.onmessage = () => {
                if(shuffleState)
                {
                    putShuffleMode(false, pointedDeviceId)
                        .then(() => {})
                        .catch((error) => { 
                            manageTooManyRequestsResponse(error, dispatch);
                            console.error('Error trying to turn off shuffle mode: ', error)
                        });
                }
            }

            worker.postMessage('start');

        // eslint-disable-next-line
        }, [running, worker, shuffleState, randomModeActive, pointedDeviceId]);
    }

    const useAvailableDevices = () => {
        const worker = useSelector(getAvailableDevicesWorker);
        
        useEffect(() => {
            if(!running || !worker)
            {
                return;
            }

            worker.onmessage = () => {
                dispatch(setAvailableDevices());
            }

            worker.postMessage('start');
        
        // eslint-disable-next-line
        }, [running, worker])
    }

    const useTooManyRequestsCooldown = () => {
        const worker = useSelector(getTooManyRequestsCooldownWorker);
        const cooldown = useSelector(getTooManyRequestsCooldown);

        useEffect(() => {
            if(!running || !worker) {
                return;
            }

            if(cooldown === 0) {
                worker.postMessage('stop');
                return;
            }

            worker.onmessage = () => {
                dispatch(setTooManyRequestsCooldown(cooldown - 1));
                if(cooldown - 1 <= 0)
                {
                    clearTooManyRequestsBlocking();
                }
            }

            worker.postMessage('start');
        // eslint-disable-next-line
        }, [running, worker, cooldown])
    }

    useUpdatePlaybackState();
    useCheckSavedAlbumsChanges();
    useDeactivateShuffleMode();
    useAvailableDevices();
    useTooManyRequestsCooldown();
}